import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, LayoutGroup } from 'framer-motion'

const NAV_LINKS = [
  { href: '/solutions', label: 'Solutions' },
  { href: '/enterprise', label: 'Enterprise' },
  { href: '/governance', label: 'Governance' },
  { href: '/investors', label: 'Investors' },
  { href: '/contact', label: 'Contact' },
]

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false)
  const [hoveredHref, setHoveredHref] = useState<string | null>(null)
  const location = useLocation()

  /* The indicator follows the hovered link; falls back to active route */
  const indicatorTarget = hoveredHref ?? location.pathname

  /* Dispatch lukhas:nav-change when route changes */
  const prevPathname = useRef(location.pathname)
  useEffect(() => {
    if (location.pathname !== prevPathname.current) {
      prevPathname.current = location.pathname
      window.dispatchEvent(
        new CustomEvent('lukhas:nav-change', {
          detail: { pathname: location.pathname },
        }),
      )
    }
  }, [location.pathname])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[rgba(8,8,18,0.78)] backdrop-blur-2xl border-b border-white/[0.06] shadow-[0_4px_30px_rgba(0,0,0,0.4)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
        {/* Brand wordmark */}
        <Link to="/" aria-label="LUKHAS home">
          <span
            className={`text-xl text-white tracking-[0.3em] transition-all duration-500 ${
              scrolled ? 'lukhas-wordmark-glow' : ''
            }`}
            style={{
              fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
              fontWeight: 100,
              textShadow: scrolled
                ? '0 0 24px rgba(96,165,250,0.6), 0 0 48px rgba(96,165,250,0.2)'
                : '0 0 20px rgba(96,165,250,0.4)',
            }}
          >
            LUKH&Lambda;S<span className="text-trust-blue">.COM</span>
          </span>
        </Link>

        {/* Nav — sliding underline indicator */}
        <LayoutGroup id="nav">
        <nav
          className="hidden md:flex items-center gap-8 relative"
          onMouseLeave={() => setHoveredHref(null)}
        >
          {NAV_LINKS.map(({ href, label }) => (
            <Link
              key={href}
              to={href}
              className="relative pb-1 text-sm tracking-[0.1em] transition-colors duration-200 text-white/60 hover:text-white data-[active=true]:text-white"
              data-active={location.pathname === href}
              aria-current={location.pathname === href ? 'page' : undefined}
              style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}
              onMouseEnter={() => setHoveredHref(href)}
            >
              {label}
              {/* Sliding underline — layoutId FLIP animates between links.
                  No AnimatePresence wrapper: allows true position slide, not fade. */}
              {indicatorTarget === href && (
                <motion.span
                  layoutId="nav-indicator"
                  className="absolute bottom-0 left-0 right-0 h-px bg-trust-blue"
                  transition={{ duration: 0.35, ease: [0.4, 0, 0.1, 1] }}
                  style={{ transformOrigin: 'left center' }}
                />
              )}
            </Link>
          ))}
        </nav>
        </LayoutGroup>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <a
            href="https://lukhas.id/login"
            className="text-sm text-white/60 hover:text-white transition-colors tracking-[0.08em]"
            style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}
          >
            Sign In
          </a>
          <Link
            to="/contact"
            className="px-4 py-1.5 rounded-full text-sm bg-trust-blue/20 border border-trust-blue/40 text-trust-blue hover:bg-trust-blue/30 transition-all tracking-[0.06em]"
            style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}
          >
            Contact Sales
          </Link>
        </div>
      </div>
    </motion.header>
  )
}
